import { useEffect, useMemo, useRef, useState } from 'react'
import { TAddon, TDuration, TPlanSummary } from './cms-types'
import { Project } from './project'
import { paymentStrings } from './strings/payment-strings'
import { getPriceIds } from 'components/util/getPriceIds' // we need this to make JSX compile
import { AddonSlug } from './planSlugs'
import useLocalStorageState from 'use-local-storage-state'
import {
  getCheckoutStorageKey,
  getTenantHeaders,
  useTenantConfig,
} from './tenant'
import { getCalculateBillingAddress } from './utils/calculateBillingAddress'
import { API } from 'project/api'

export const getDirectorPrice = (
  allAddons: TAddon[] | null,
  duration: TDuration,
): number => {
  const dsaAddon = allAddons?.find(
    (v) => v.slug === AddonSlug.DIRECTORS_SERVICE_ADDRESS,
  )
  if (!dsaAddon?.pricing) return 0
  return duration === 'month'
    ? dsaAddon.pricing.month_price
    : dsaAddon.pricing.year_price
}

type Coupon = {
  code: string
  discountedTotal: number | null //New basket total including coupon
  discountedVAT: number | null // Coupon discounted VAT
  discountAmount: number // Coupon discounted amount
  discountDescription: string // Coupon description
}
export let coupon: Coupon | null

// checkout/calculate response, amounts in pence/cents
type Calculation = {
  sub_total: number
  tax: number
  tax_description: string
  total: number
  discount_amount: number
  discount_description: string
}

// CheckoutSummary and PaymentForm both use this hook, so share one request
// per identical basket rather than calling calculate twice
const pendingCalculations: Record<string, Promise<Calculation>> = {}
const requestCalculation = (body: string): Promise<Calculation> => {
  if (!pendingCalculations[body]) {
    pendingCalculations[body] = fetch(`${Project.api}checkout/calculate`, {
      body,
      headers: getTenantHeaders(),
      method: 'POST',
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .finally(() => {
        delete pendingCalculations[body]
      })
  }
  return pendingCalculations[body]
}

const getBillingAddress = (tenantCountry: string) =>
  getCalculateBillingAddress(
    API.storage.getItemSync(getCheckoutStorageKey()),
    tenantCountry,
  )

export default function (
  plan: TPlanSummary | null,
  allAddons: TAddon[] | null,
  duration: TDuration,
  addons: string[],
  additionalPlans?: TPlanSummary[],
  directorsCount?: number,
) {
  const tenantConfig = useTenantConfig()
  const [discount, setDiscount] = useLocalStorageState<number | null>(
    'discount',
    { defaultValue: null, storageSync: false },
  )
  const [activeCoupon, setActiveCoupon] = useLocalStorageState<string>(
    'activeCoupon',
    { defaultValue: '', storageSync: false },
  )
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState('')
  const [_, setActiveCouponInput] = useLocalStorageState<string>(
    'couponInput',
    { defaultValue: '', storageSync: false },
  )
  const [discountDescription, setDiscountDescription] =
    useLocalStorageState<string>('discountDescription', {
      defaultValue: '',
      storageSync: false,
    })
  const [calculation, setCalculation] = useState<Calculation | null>(null)
  const [calculating, setCalculating] = useState(true)
  const [billingLocation, setBillingLocation] = useState('')
  const intervalRef = useRef()

  const setInitialCoupon = useRef(false)
  if (!setInitialCoupon.current) {
    setInitialCoupon.current = true
    if (discount !== null) {
      // Totals are filled in when the next calculation comes back
      coupon = {
        code: activeCoupon,
        discountAmount: discount,
        discountDescription: discountDescription,
        discountedTotal: null,
        discountedVAT: null,
      }
    }
  }

  useEffect(() => {
    if (intervalRef?.current) {
      clearInterval(intervalRef?.current)
    }
    const interval = setInterval(() => {
      if (coupon?.code !== activeCoupon) {
        if (!coupon) {
          setActiveCoupon('')
          setDiscount(null)
          setDiscountDescription('')
        } else {
          setActiveCoupon(coupon?.code)
          setDiscount(coupon?.discountAmount)
          setDiscountDescription(coupon?.discountDescription)
        }
      }
    }, 500)
    return () => clearInterval(interval)
  }, [activeCoupon])

  // Tax depends on where the customer is billed. The checkout forms save the
  // billing address to storage as it's typed, so watch its country and postcode
  useEffect(() => {
    const readBillingLocation = () => {
      const address = getBillingAddress(tenantConfig.country)
      setBillingLocation(`${address.country}|${address.postal_code || ''}`)
    }
    readBillingLocation()
    const interval = setInterval(readBillingLocation, 1000)
    return () => clearInterval(interval)
  }, [tenantConfig.country])

  const planPrice = plan
    ? duration === 'month'
      ? plan.pricing.month_price
      : plan.pricing.year_price
    : 0

  const addonPrice = useMemo(() => {
    let price = 0
    if (!allAddons) return 0
    addons.map((addon) => {
      if (addon === AddonSlug.DIRECTORS_SERVICE_ADDRESS) {
        return
      }
      const addonObj = allAddons.find((v) => v.slug === addon)
      const addonPrice = addonObj
        ? duration === 'month'
          ? addonObj.pricing.month_price
          : addonObj.pricing.year_price
        : 0

      price += addonPrice
    })
    if (directorsCount) {
      price += directorsCount * getDirectorPrice(allAddons, duration)
    }
    return price
  }, [addons, allAddons, duration, directorsCount])

  const getCalculateBody = (voucher: string) => {
    const ids = getPriceIds(
      [plan!],
      allAddons,
      plan?.slug || null,
      addons,
      duration,
      undefined,
      directorsCount,
    )
    const additionalPlanIds =
      additionalPlans
        ?.map((p) =>
          duration === 'month'
            ? p.pricing.month_price_id
            : p.pricing.year_price_id,
        )
        .filter((v) => !!v) || []
    return JSON.stringify({
      additional_plans: additionalPlanIds,
      addons: ids?.addons.filter((v) => !!v.id),
      billing_address: getBillingAddress(tenantConfig.country),
      product_id: ids?.product_id,
      // The API rejects a blank voucher, so leave it out without a coupon
      ...(voucher && { voucher }),
    })
  }

  // Tax, totals and the discount all come from checkout/calculate, so
  // recalculate whenever anything that affects them changes
  const addonsKey = addons.join()
  const additionalPlansKey = additionalPlans?.map((p) => p.slug).join()
  useEffect(() => {
    if (!plan) return
    let cancelled = false
    setCalculating(true)
    const timeout = setTimeout(() => {
      requestCalculation(getCalculateBody(activeCoupon))
        .then((res) => {
          if (cancelled) return
          setCalculation(res)
          if (coupon && coupon.code === activeCoupon) {
            coupon.discountedTotal = res.total
            coupon.discountedVAT = res.tax
          }
        })
        .catch(() => {
          if (!cancelled) setCalculation(null)
        })
        .finally(() => {
          if (!cancelled) setCalculating(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
    // getCalculateBody reads these values; the keys stand in for the arrays
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    plan?.slug,
    duration,
    addonsKey,
    additionalPlansKey,
    directorsCount,
    activeCoupon,
    billingLocation,
  ])

  // Never estimate: no figures until calculate has answered for this basket
  const ready = !calculating && !!calculation
  const vatPrice = ready ? calculation.tax : null
  const totalPrice = ready ? calculation.total : null
  // Tax label from Chargebee, e.g. "VAT @ 20%" or "GST @ 10%". Kept from the
  // last calculation while a new one is in flight
  const taxDescription = calculation?.tax_description ?? null

  const clearCoupon = () => {
    setActiveCouponInput('')
    setCouponError('')
    coupon = null
  }

  const checkCoupon = (code: string) => {
    setCouponError('')
    coupon = null // clear erros and previously applied coupon
    setCouponLoading(true)
    requestCalculation(getCalculateBody(code))
      .then((res) => {
        setCouponLoading(false)
        if (res.discount_amount) {
          coupon = {
            code,
            discountAmount: res.discount_amount,
            discountDescription: res.discount_description,
            discountedTotal: res.total,
            discountedVAT: res.tax,
          }
          setActiveCoupon(code)
          setDiscount(res.discount_amount)
          setDiscountDescription(res.discount_description)
          setCalculation(res)
          setCouponError('')
        } else {
          throw ''
        }
      })
      .catch(() => {
        setDiscount(0)
        setCouponLoading(false)
        setCouponError(`${paymentStrings.couponError}`)
      })
  }

  return {
    activeCoupon,
    addonPrice,
    calculating,
    checkCoupon,
    clearCoupon,
    couponError,
    couponLoading,
    discount: activeCoupon && calculation ? calculation.discount_amount : null,
    discountDescription,
    planPrice,
    taxDescription,
    totalPrice,
    vatPrice,
  }
}
